import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FrontPageLogo from "../../components/Logo/Logo";
import QuestionHeader from "../../components/AlternateQuestionHeader/AlternateQuestionHeader";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import BrushIcon from "@mui/icons-material/Brush";
import { getRandomTsarName } from "../../shared/helper/functions";
import "./DeckTutorial.css";

const textMaxW = 620;

function Section({ children }: { children: React.ReactNode }) {
  return (
    <Stack spacing={2} sx={{ width: "100%", maxWidth: textMaxW, mt: 6 }}>
      {children}
    </Stack>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="h5" sx={{ fontWeight: 700 }}>
      {children}
    </Typography>
  );
}

function InfoPaper({
  children,
  color,
}: {
  children: React.ReactNode;
  color?: string;
}) {
  return (
    <Paper
      sx={{
        p: 2,
        backgroundColor: color || "rgba(255,255,255,0.08)",
        borderLeft: color ? `4px solid ${color}` : undefined,
      }}
    >
      {children}
    </Paper>
  );
}

function RawVsRendered({
  raw,
  question,
  tsarName,
  isTsar,
  isMobile,
}: {
  raw: string;
  question: { forTsar: string; aboutTsar: string };
  tsarName: string;
  isTsar: boolean;
  isMobile: boolean;
}) {
  return (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ opacity: 0.6 }}>
        Raw text:
      </Typography>
      <Paper sx={{ p: 1.5, backgroundColor: "rgba(0,0,0,0.06)" }}>
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}
        >
          {raw}
        </Typography>
      </Paper>
      <Typography variant="body2" sx={{ opacity: 0.6, mt: 1 }}>
        Rendered in-game:
      </Typography>
      <QuestionHeader
        question={question}
        tsarName={tsarName}
        isTsar={isTsar}
        isMobile={isMobile}
      />
    </Stack>
  );
}

export default function DeckTutorial() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const history = useHistory();
  const [tsarName, setTsarName] = useState(getRandomTsarName());

  const refreshTsar = () => setTsarName(getRandomTsarName());

  return (
    <div className="column rank5background deckTutorial">
      <Stack alignItems="center" sx={{ width: "100%" }}>
        <FrontPageLogo />

        {/* Section 1: Hero */}
        <Section>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 800, textAlign: "center" }}>
            {t("deck_tutorial_hero_title")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: "center", opacity: 0.8 }}
          >
            {t("deck_tutorial_hero_subtitle")}
          </Typography>

          {/* Quick-nav buttons */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 2, flexWrap: "wrap", justifyContent: "center", gap: 1 }}
          >
            {[
              { label: t("deck_tutorial_nav_questions"), target: "questions-section" },
              { label: t("deck_tutorial_nav_answers"), target: "answers-section" },
              { label: t("deck_tutorial_nav_friend"), target: "friend-section" },
            ].map((nav) => (
              <Chip
                key={nav.target}
                label={nav.label}
                color="primary"
                clickable
                onClick={() =>
                  document.getElementById(nav.target)?.scrollIntoView({ behavior: "smooth" })
                }
              />
            ))}
          </Stack>
        </Section>

        {/* Section 2: What is a Deck? */}
        <Section>
          <SectionHeader>{t("deck_tutorial_what_is_deck")}</SectionHeader>
          <Typography variant="body1">
            {t("deck_tutorial_what_is_deck_body")}
          </Typography>

          {/* Hierarchy visual */}
          <Paper sx={{ p: 2.5, backgroundColor: "rgba(255,255,255,0.08)" }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("deck_tutorial_hierarchy_example")}
              </Typography>
              <Box sx={{ pl: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                  My Party Deck
                </Typography>
                <Box sx={{ pl: 2, mt: 1, borderLeft: "2px solid rgba(236,119,119,0.3)" }}>
                  <Stack spacing={1.5}>
                    <Box>
                      <Chip label="food" size="small" color="primary" sx={{ mb: 0.5 }} />
                      <Typography variant="body2" sx={{ pl: 1, fontSize: 13 }}>
                        Questions: "What food would you never eat?"
                      </Typography>
                      <Typography variant="body2" sx={{ pl: 1, fontSize: 13, opacity: 0.7 }}>
                        Answers: Pizza, Sushi, Broccoli, Steak, Ice Cream
                      </Typography>
                    </Box>
                    <Box>
                      <Chip label="hypotheticals" size="small" color="primary" sx={{ mb: 0.5 }} />
                      <Typography variant="body2" sx={{ pl: 1, fontSize: 13 }}>
                        Questions: "If you had a superpower, what would it be?"
                      </Typography>
                      <Typography variant="body2" sx={{ pl: 1, fontSize: 13, opacity: 0.7 }}>
                        Answers: Flying, Invisibility, Teleportation, Mind reading, Time travel
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </Paper>

          <InfoPaper>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t("deck_tutorial_min_cards_rule")}
            </Typography>
          </InfoPaper>
        </Section>

        {/* Section 3: How Questions Work */}
        <Section>
          <SectionHeader><span id="questions-section">{t("deck_tutorial_questions_title")}</span></SectionHeader>
          <Typography variant="body1">
            {t("deck_tutorial_questions_body")}
          </Typography>
          <Typography variant="body1">
            {t("deck_tutorial_questions_why")}
          </Typography>

          {/* Interactive demo: side by side question headers */}
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              "for the tsar" (what the person the turn is about sees):
            </Typography>
            <QuestionHeader
              question={{
                forTsar: "Which gym exercise do <h>you</h> like the most?",
                aboutTsar: "Which gym exercise does <h>{{tsar}}</h> like the most?",
              }}
              tsarName={tsarName}
              isTsar={true}
              isMobile={isMobile}
            />
            <Typography variant="body2" sx={{ opacity: 0.6, mt: 1 }}>
              "about the tsar" (what everyone else sees — uses the tsar's name):
            </Typography>
            <QuestionHeader
              question={{
                forTsar: "Which gym exercise do <h>you</h> like the most?",
                aboutTsar: "Which gym exercise does <h>{{tsar}}</h> like the most?",
              }}
              tsarName={tsarName}
              isTsar={false}
              isMobile={isMobile}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton onClick={refreshTsar} size="small">
                <RefreshIcon color="primary" />
              </IconButton>
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {t("deck_tutorial_refresh_tsar")}
              </Typography>
            </Stack>
          </Stack>
        </Section>

        {/* Section 4: The AI Shortcut (moved before formatting so people know they can skip) */}
        <Section>
          <SectionHeader>{t("deck_tutorial_ai_title")}</SectionHeader>
          <Typography variant="body1">
            {t("deck_tutorial_ai_body")}
          </Typography>

          {/* Before/After mockup */}
          <Paper sx={{ p: 2, backgroundColor: "rgba(255,255,255,0.08)" }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>You type:</Typography>
                <Paper sx={{ p: 1.5, backgroundColor: "rgba(0,0,0,0.06)" }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 13 }}>
                    what food would you never eat
                  </Typography>
                </Paper>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  disabled
                  startIcon={<AutoFixHighIcon />}
                  sx={{
                    background: "linear-gradient(135deg, #ec7777 0%, #d45f9a 100%) !important",
                    color: "white !important",
                    opacity: "1 !important",
                  }}
                >
                  Format with AI
                </Button>
                <ArrowForwardIcon sx={{ opacity: 0.4 }} />
              </Stack>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>forTsar:</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  What food would &lt;h&gt;you&lt;/h&gt; never eat?
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>aboutTsar:</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  {`What food would <h>{{tsar}}</h> never eat?`}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* German example */}
          <Paper sx={{ p: 2, backgroundColor: "rgba(255,255,255,0.08)" }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>You type (German):</Typography>
                <Paper sx={{ p: 1.5, backgroundColor: "rgba(0,0,0,0.06)" }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 13 }}>
                    Welches Tier würdest du am ehesten essen
                  </Typography>
                </Paper>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>forTsar:</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  Welches Tier würdest &lt;h&gt;du&lt;/h&gt; am ehesten essen?
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>aboutTsar:</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  {`Welches Tier würde <h>{{tsar}}</h> am ehesten essen?`}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <InfoPaper>
            <Typography variant="body2">
              {t("deck_tutorial_ai_tip")}
            </Typography>
          </InfoPaper>

        </Section>

        {/* Section 5: Formatting (detailed, for manual formatting) */}
        <Section>
          <SectionHeader>{t("deck_tutorial_formatting_title")}</SectionHeader>
          <Typography variant="body1">
            {t("deck_tutorial_formatting_body")}
          </Typography>

          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>&lt;h&gt;...&lt;/h&gt;</strong> wraps text in the game's primary color for emphasis. Use it on pronouns (you, your, they, their) and the tsar's name.
            </Typography>
            <Typography variant="body2">
              <strong>{"{{tsar}}"}</strong> gets replaced with the actual tsar's name at runtime. Always wrap it: <code>&lt;h&gt;{"{{tsar}}"}&lt;/h&gt;</code>
            </Typography>
          </Stack>

          <RawVsRendered
            raw={`What would <h>{{tsar}}</h> do if <h>they</h> won the lottery?`}
            question={{
              forTsar: "What would <h>you</h> do if <h>you</h> won the lottery?",
              aboutTsar: "What would <h>{{tsar}}</h> do if <h>they</h> won the lottery?",
            }}
            tsarName={tsarName}
            isTsar={false}
            isMobile={isMobile}
          />

          {/* Good examples */}
          <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
            Good examples:
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: "rgba(255,255,255,0.08)" }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>EN, forTsar:</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  If &lt;h&gt;you&lt;/h&gt; could double the size of one of &lt;h&gt;your&lt;/h&gt; muscles, which one would &lt;h&gt;you&lt;/h&gt; choose?
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>EN, aboutTsar:</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  {`If <h>{{tsar}}</h> could double the size of one of <h>their</h> muscles, which one would <h>they</h> choose?`}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>DE, forTsar:</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  Welches Tier würdest &lt;h&gt;du&lt;/h&gt; am ehesten als Haustier halten?
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>DE, aboutTsar:</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  {`Welches Tier würde <h>{{tsar}}</h> am ehesten als Haustier halten?`}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Bad examples */}
          <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
            Common mistakes:
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: "rgba(231,81,81,0.08)", borderLeft: "4px solid #e75151" }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12, color: "#e75151" }}>
                  Which gym exercise do you like the most?
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  Missing &lt;h&gt; tags. Pronouns won't be highlighted in-game.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12, color: "#e75151" }}>
                  aboutTsar: (empty)
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  Only one side written. Both forTsar and aboutTsar are required.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12, color: "#e75151" }}>
                  {`If <h>{{tsar}}</h> could double the size of one of your muscles`}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  Wrong pronoun. In the aboutTsar version, "your" should be "their".
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <InfoPaper>
            <Typography variant="body2">
              {t("deck_tutorial_formatting_tip")}
            </Typography>
          </InfoPaper>
        </Section>

        {/* Section 6: Answer Cards */}
        <Section>
          <SectionHeader><span id="answers-section">{t("deck_tutorial_answers_title")}</span></SectionHeader>
          <Typography variant="body1">
            {t("deck_tutorial_answers_body")}
          </Typography>
        </Section>

        {/* Section 6b: Friend Token */}
        <Section>
          <SectionHeader><span id="friend-section">{t("deck_tutorial_friend_token_title")}</span></SectionHeader>
          <Typography variant="body1">
            {t("deck_tutorial_friend_token_body")}
          </Typography>

          <Paper sx={{ p: 2, backgroundColor: "rgba(255,255,255,0.08)" }}>
            <Stack spacing={1.5}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("deck_tutorial_friend_token_example_header")}
              </Typography>
              <Box sx={{ pl: 1.5, borderLeft: "2px solid rgba(236,119,119,0.3)" }}>
                <Typography variant="body2" sx={{ fontSize: 13 }}>
                  {t("deck_tutorial_friend_token_example_card")}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 13, mt: 1 }}>
                  {t("deck_tutorial_friend_token_example_resolve")}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <InfoPaper>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t("deck_tutorial_friend_token_no_duplicate_title")}
            </Typography>
            <Typography variant="body2">
              {t("deck_tutorial_friend_token_no_duplicate_body")}
            </Typography>
          </InfoPaper>

          <InfoPaper>
            <Typography variant="body2">
              {t("deck_tutorial_friend_token_button_tip")}
            </Typography>
          </InfoPaper>
        </Section>

        {/* Section 7: Categories and Card Reuse */}
        <Section>
          <SectionHeader>{t("deck_tutorial_categories_title")}</SectionHeader>
          <Typography variant="body1">
            {t("deck_tutorial_categories_body")}
          </Typography>

          {/* Category chips visual */}
          <Paper sx={{ p: 2, backgroundColor: "rgba(255,255,255,0.08)" }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                <Chip label="food" color="primary" size="small" />
                <Chip label="relationships" variant="outlined" size="small" />
                <Chip label="hypotheticals" variant="outlined" size="small" />
                <Chip label="gym" variant="outlined" size="small" />
              </Stack>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>
                  Category "food" could contain:
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  <Typography variant="body2">Question: "What food would you never eat?"</Typography>
                  <Typography variant="body2">Answers: Pizza, Sushi, Broccoli, Steak, Ice Cream</Typography>
                </Stack>
              </Box>
            </Stack>
          </Paper>

          <InfoPaper>
            <Typography variant="body2">
              {t("deck_tutorial_categories_tip")}
            </Typography>
          </InfoPaper>

          {/* Card reuse / many-to-many explanation */}
          <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
            {t("deck_tutorial_reuse_title")}
          </Typography>
          <Typography variant="body1">
            {t("deck_tutorial_reuse_body")}
          </Typography>

          <Paper sx={{ p: 2, backgroundColor: "rgba(255,255,255,0.08)" }}>
            <Stack spacing={1.5}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("deck_tutorial_reuse_example_header")}
              </Typography>
              <Box sx={{ pl: 1.5, borderLeft: "2px solid rgba(236,119,119,0.3)" }}>
                <Typography variant="body2" sx={{ fontSize: 13 }}>
                  Answer card: <strong>"Pizza"</strong>
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, pl: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: 13, opacity: 0.7 }}>tagged with:</Typography>
                  <Chip label="first date" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                  <Chip label="food" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                </Stack>
                <Typography variant="body2" sx={{ fontSize: 12, opacity: 0.6, mt: 1.5 }}>
                  so it shows up in both of these questions:
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5, pl: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    "What would you bring to a first date?"
                    <Chip label="first date" size="small" variant="outlined" sx={{ height: 18, fontSize: 10, ml: 0.5 }} />
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    "What's your guilty pleasure food?"
                    <Chip label="food" size="small" variant="outlined" sx={{ height: 18, fontSize: 10, ml: 0.5 }} />
                  </Typography>
                </Stack>
              </Box>
              <Typography variant="body2" sx={{ fontSize: 12, opacity: 0.6, mt: 0.5 }}>
                Different categories, different questions, but the same "Pizza" card works in both contexts. Write once, reuse everywhere.
              </Typography>
            </Stack>
          </Paper>

          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {t("deck_tutorial_reuse_benefit")}
          </Typography>
        </Section>

        {/* Section 8: Preview & Polish */}
        <Section>
          <SectionHeader>{t("deck_tutorial_preview_title")}</SectionHeader>
          <Typography variant="body1">
            {t("deck_tutorial_preview_body")}
          </Typography>
        </Section>

        {/* Section 9: Picasso Mode (Bonus) */}
        <Section>
          <SectionHeader>
            <Stack direction="row" alignItems="center" spacing={1}>
              <BrushIcon color="primary" />
              <span>{t("deck_tutorial_picasso_title")}</span>
            </Stack>
          </SectionHeader>
          <Typography variant="body1">
            {t("deck_tutorial_picasso_body")}
          </Typography>

          {/* Picasso checkbox explanation */}
          <Paper sx={{ p: 2, backgroundColor: "rgba(255,255,255,0.08)" }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckBoxIcon color="primary" sx={{ fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("deck_tutorial_picasso_checkbox")}
                </Typography>
              </Stack>
              <Typography variant="body2">
                {t("deck_tutorial_picasso_checkbox_body")}
              </Typography>
            </Stack>
          </Paper>

          {/* Good Picasso examples */}
          <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
            {t("deck_tutorial_picasso_good_title")}
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: "rgba(146,220,103,0.08)", borderLeft: "4px solid #92dc67" }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckBoxIcon sx={{ fontSize: 16, color: "#92dc67" }} />
                <Typography variant="body2">"What does your happy place look like?"</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckBoxIcon sx={{ fontSize: 16, color: "#92dc67" }} />
                <Typography variant="body2">"What is the worst thing that happened to you in high school?"</Typography>
              </Stack>
              <Typography variant="caption" sx={{ opacity: 0.6, pl: 3.5 }}>
                {t("deck_tutorial_picasso_good_why")}
              </Typography>
            </Stack>
          </Paper>

          {/* Bad Picasso examples */}
          <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
            {t("deck_tutorial_picasso_bad_title")}
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: "rgba(231,81,81,0.08)", borderLeft: "4px solid #e75151" }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckBoxOutlineBlankIcon sx={{ fontSize: 16, color: "#e75151" }} />
                <Typography variant="body2">"What do you value most in a friend?"</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckBoxOutlineBlankIcon sx={{ fontSize: 16, color: "#e75151" }} />
                <Typography variant="body2">"What's your favorite ice cream flavor?"</Typography>
              </Stack>
              <Typography variant="caption" sx={{ opacity: 0.6, pl: 3.5 }}>
                {t("deck_tutorial_picasso_bad_why")}
              </Typography>
            </Stack>
          </Paper>

          {/* Versatile questions */}
          <InfoPaper>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t("deck_tutorial_picasso_versatile_title")}
            </Typography>
            <Typography variant="body2">
              {t("deck_tutorial_picasso_versatile_body")}
            </Typography>
          </InfoPaper>

          <InfoPaper>
            <Typography variant="body2">
              {t("deck_tutorial_picasso_no_answers")}
            </Typography>
          </InfoPaper>
        </Section>

        {/* Section 10: CTA */}
        <Section>
          <Stack spacing={2} alignItems="center" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t("deck_tutorial_cta_title")}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => history.push("/profile")}
              sx={{ textTransform: "none", px: 4 }}
            >
              {t("deck_tutorial_cta_button")}
            </Button>
            <Button
              variant="text"
              onClick={() => history.push("/howTo")}
              sx={{ textTransform: "none", opacity: 0.7 }}
            >
              {t("deck_tutorial_back_to_howto")}
            </Button>
          </Stack>
        </Section>
      </Stack>
    </div>
  );
}
